import React, {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import AddRoundedIcon from '@mui/icons-material/AddRounded';
import BarChartRoundedIcon from '@mui/icons-material/BarChartRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import FunctionsRoundedIcon from '@mui/icons-material/FunctionsRounded';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import KeyboardArrowLeftRoundedIcon from '@mui/icons-material/KeyboardArrowLeftRounded';
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';
import ShowChartRoundedIcon from '@mui/icons-material/ShowChartRounded';
import {
  Box,
  Button,
  Card,
  CardContent,
  ClickAwayListener,
  Grid,
  Grow,
  IconButton,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { showSnackbar } from '@/redux/snackbarSlice';
import { useLazyGetEnergyConsumptionQuery } from '../../../../api/energy-analysis/calculateEnergySavingsApiSlice';

import { durationMapping } from '../durationMapping';

import { DateTime } from 'luxon';
import EnergyAnalysisComparisonSeriesChart from '@/components/highcharts/powerAnalyser/EnergyAnalysisComparisonSeriesChart';
import EnergyAnalyzerFilterInput from '@/components/forms/energyxplorer/EnergyAnalyzerFilterInput';
import EnergyTreeNodeAutocomplete from '@/features/energyxplorer/components/EnergyTreeNodeAutocomplete';
import SingleDateSelector from '@/components/time-range-selector/SingleDateRangeSelector';
import TimestampSummaryWarningIcon from '@/features/energyxplorer/components/TimestampsSummaryWarningIcon';

/**
 * formatDate - Formats a Luxon DateTime object to a string in the format "d. MMMM"
 * @param {DateTime} date - The Luxon DateTime object to format
 */
const formatDate = (date) => {
  const day = date.setLocale('da').toFormat('d.');
  const month = date.setLocale('da').toFormat('MMMM');
  return `${day} ${month}`;
};

/**
 * formatNumber - Formats a number to a string with Danish number formatting
 * @param {number} number - The number to format
 */
const formatNumber = (number) => {
  return new Intl.NumberFormat('da-DK').format(number);
};

const ComparisonAnalysisTab = ({
  selectedAccount,
  selectedEnergyGroup,
  timeRange,
  duration,
  setTimestampSummary,
  setTimestampSummarySetBy,
}) => {
  const { t } = useTranslation('energyxplorer-analysis-tabs');
  const dispatch = useDispatch();

  //===API====//
  const [
    getEnergyConsumptionData,
    {
      isLoading: isLoadingEnergyConsumptionData,
      isFetching: isFetchingEnergyConsumptionData,
    },
  ] = useLazyGetEnergyConsumptionQuery();

  //===State====//
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [nodeToAnalyse, setNodeToAnalyse] = useState(null);
  const [comparisonItems, setComparisonItems] = useState([]);
  const [comparisonFilters, setComparisonFilters] = useState({});
  const [comparisonTimeRange, setComparisonTimeRange] = useState({
    start: timeRange.start,
    end: timeRange.end,
  });

  const [aggregate, setAggregate] = useState(null);
  const [viewMode, setViewMode] = useState(null); // 'column' or 'line'

  const [selectedRange, setSelectedRange] = useState([
    DateTime.fromMillis(timeRange.start).setZone('Europe/Copenhagen'),
    DateTime.fromMillis(timeRange.end).setZone('Europe/Copenhagen'),
  ]);

  useEffect(() => {
    setSelectedRange([
      DateTime.fromMillis(timeRange.start).setZone('Europe/Copenhagen'),
      DateTime.fromMillis(timeRange.end).setZone('Europe/Copenhagen'),
    ]);
  }, [timeRange]);

  const [data, setData] = useState(null);
  const [comparisonEnergyConsumptionData, setComparisonEnergyConsumptionData] =
    useState([]); // For comparison data

  const isComparingSameNode = useMemo(
    () =>
      comparisonItems.some(
        (item) =>
          item?.node?.energyTreeNodeId === nodeToAnalyse?.energyTreeNodeId,
      ),
    [comparisonItems, nodeToAnalyse],
  );

  const getDurationConfig = (durationDays) => {
    return durationMapping.find(
      (entry) => durationDays >= entry.minDays && durationDays < entry.maxDays,
    );
  };

  const anchorRef = useRef(null);
  const [openAggregationMenu, setOpenAggregationMenu] = useState(false);
  const [dataSeriesAggregate, setDataSeriesAggregate] = useState({
    value: 'mean',
    label: t('common.mean'),
  });

  const handleToggleAggregationMenu = () => {
    setOpenAggregationMenu((prev) => !prev);
  };

  const handleCloseAggregationMenu = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpenAggregationMenu(false);
  };
  const handleSelectAggregation = (option) => {
    setOpenAggregationMenu(false);
    setDataSeriesAggregate(option);
  };

  const options = [
    { value: 'sum', label: t('common.sum') },
    { value: 'mean', label: t('common.mean') },
    { value: 'min', label: t('common.min') },
    { value: 'max', label: t('common.max') },
  ];

  //===Memoized Values====//
  const config = useMemo(() => {
    if (typeof duration !== 'number') return null;
    return getDurationConfig(duration) || null; // Ensure it never returns undefined
  }, [duration]);

  useEffect(() => {
    if (!config) return;

    // Determine and set valid initial values
    setViewMode((prev) => {
      if (!prev || !config.chartOptions.includes(prev)) {
        return config.defaultChart;
      }
      return prev;
    });

    setAggregate((prev) => {
      const validAggregates =
        config?.xAxisOptions?.[viewMode || config.defaultChart] || [];
      if (!prev || !validAggregates.includes(prev)) {
        return config.defaultXAxis?.[viewMode || config.defaultChart] || 'hour';
      }
      return prev;
    });
  }, [config, viewMode]);

  const validChartOptions = config?.chartOptions || []; // Available chart types
  const validXAxisOptions = config?.xAxisOptions?.[viewMode] || []; // Available X-Axis granularity

  const fetchEnergyConsumption = useCallback(
    async ({ nodeId, start, end, filters, aggregate }) => {
      try {
        const response = await getEnergyConsumptionData({
          accountId: selectedAccount?.AccountId,
          energyGroupId: selectedEnergyGroup?.energyGroupId,
          nodeId,
          start,
          end,
          aggregate,
          ...filters,
        }).unwrap();

        setTimestampSummary(response?.EnergySummary?.TimestampsSummary);
        setTimestampSummarySetBy('Sammenligningsanalysen');

        return {
          ...response,
          metadata: {
            ...filters,
            id: Date.now(),
          },
        };
      } catch (error) {
        console.error('Failed to fetch energy data', error);
        setTimestampSummary(null);
        setTimestampSummarySetBy(null);
        dispatch(
          showSnackbar({
            message: 'Der opstod en fejl i indhentingen af forbrugsdata',
            severity: 'error',
          }),
        );
        return null;
      }
    },
    [
      getEnergyConsumptionData,
      selectedAccount,
      selectedEnergyGroup,
      dispatch,
      setTimestampSummary,
      setTimestampSummarySetBy,
    ],
  );

  /**
   *
   * @returns {Promise<void>} - A promise that resolves when the comparison filter is applied.
   * @description - This function fetches energy consumption data for all comparison items and updates the state with the new data.
   * It uses the `fetchEnergyConsumption` function to get the data for each item in the `comparisonItems` array.
   * If there are no comparison items, it logs an error message.
   *
   */
  const handleApplyComparisonFilter = async () => {
    if (comparisonItems.length === 0) {
      console.error('No comparison items to apply filters to.');
      return;
    }

    const newComparisonItems = await Promise.all(
      comparisonItems.map((item) =>
        fetchEnergyConsumption({
          nodeId: item.node?.energyTreeNodeId,
          start: comparisonTimeRange.start,
          end: comparisonTimeRange.end,
          filters: comparisonFilters,
          aggregate,
        }),
      ),
    );
    setData(newComparisonItems.filter(Boolean));
  };

  const addComparisonItem = () => {
    setComparisonItems((prevItems) => [
      ...prevItems, // Keep existing items
      { node: null }, // Add new empty comparison
    ]);
  };

  const removeComparisonItem = (index) => {
    setComparisonItems((prevItems) =>
      prevItems.filter((_, idx) => idx !== index),
    );
  };

  const updateComparisonItem = (index, key, value) => {
    setComparisonItems((prevItems) => {
      const updatedItems = [...prevItems];
      updatedItems[index] = { ...updatedItems[index], [key]: value };
      return updatedItems;
    });
  };

  const getTooltipTitle = (item) => {
    const labelMap = {
      hour: t('common.hour'),
      day: t('common.day'),
      week: t('common.week'),
      month: t('common.month'),
    };

    const isValidXAxis = validXAxisOptions.includes(item);

    // Tooltip logic
    if (item === 'hour' && viewMode !== 'line') {
      return t('comparison_analysis.only_available_for_linear_series');
    }

    if (['day', 'week', 'month'].includes(item) && viewMode === 'line') {
      return t('comparison_analysis.only_available_for_column_series');
    }

    if (!isValidXAxis) {
      return t('comparison_analysis.not_available_for_selected_period');
    }

    return `${t('comparison_analysis.show_per')} ${labelMap[item] || item}`;
  };

  //===Effects====//
  useEffect(() => {
    if (!nodeToAnalyse || !timeRange || !aggregate || !comparisonFilters)
      return;

    const fetchNodeToAnalyseData = async () => {
      const result = await fetchEnergyConsumption({
        nodeId: nodeToAnalyse.energyTreeNodeId,
        start: timeRange.start,
        end: timeRange.end,
        filters: comparisonFilters,
        aggregate,
      });

      if (result && result !== null) {
        setData(result);
      } else {
        setData([]);
      }
    };

    fetchNodeToAnalyseData();
  }, [
    nodeToAnalyse,
    timeRange,
    aggregate,
    viewMode,
    comparisonFilters,
    fetchEnergyConsumption,
  ]);

  useEffect(() => {
    const fetchAllComparisonItems = async () => {
      const newComparisonData = await Promise.all(
        comparisonItems.map(async (item) => {
          const result = await fetchEnergyConsumption({
            nodeId: item.node.energyTreeNodeId,
            start: comparisonTimeRange.start,
            end: comparisonTimeRange.end,
            filters: comparisonFilters, // from your filter state
            aggregate,
            metadataId: item.id, // optional, to track it
          });

          return result;
        }),
      );

      setComparisonEnergyConsumptionData(newComparisonData.filter(Boolean));
    };

    fetchAllComparisonItems();
  }, [
    comparisonItems,
    comparisonFilters,
    comparisonTimeRange,
    aggregate,
    viewMode,
    fetchEnergyConsumption,
  ]); // only runs when items are added/removed

  useEffect(() => {
    if (
      !nodeToAnalyse ||
      !selectedEnergyGroup.energyTreeNodes.find(
        (node) => node.energyTreeNodeId === nodeToAnalyse.energyTreeNodeId,
      )
    ) {
      setNodeToAnalyse(selectedEnergyGroup.energyTreeNodes[0]); // Set first available node as default
    }
  }, [nodeToAnalyse, selectedEnergyGroup]);

  useEffect(() => {
    if (!config) return;

    const defaultChart = config.defaultChart;
    const currentView = viewMode || defaultChart;
    const validAggregates = config?.xAxisOptions?.[currentView] || [];
    const defaultXAxis = config?.defaultXAxis?.[currentView] || 'hour';

    setViewMode((prev) => {
      if (!prev || !config.chartOptions.includes(prev)) {
        return defaultChart;
      }
      return prev;
    });

    setAggregate((prev) => {
      if (!prev || !validAggregates.includes(prev)) {
        return defaultXAxis;
      }
      return prev;
    });
  }, [config, viewMode]);

  if (!aggregate || !viewMode) {
    return null; // or fallback UI
  }

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid
          item
          md={12}
          lg={filtersOpen ? 9 : 12}
          sx={{
            transition: 'flex-basis 0.3s ease-in-out',
          }}
        >
          <Card variant="outlined" sx={{ p: 1, borderRadius: 2 }}>
            <CardContent>
              <Stack
                direction="column"
                color="inherit"
                component={Card}
                elevation={0}
                sx={{
                  transition: 'box-shadow 0.3s ease',
                  height: '100%',
                  background: 'transparent',
                  mb: 4,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    width: '100%',
                  }}
                >
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Typography
                        variant="h6"
                        color="textSecondary"
                        fontWeight="bold"
                      >
                        {nodeToAnalyse?.name}
                      </Typography>
                      <TimestampSummaryWarningIcon node={nodeToAnalyse} />
                      <Tooltip
                        title={`${t('display_sum_of_consumption_over_time')} ${nodeToAnalyse?.name}`}
                        placement="right"
                      >
                        <InfoRoundedIcon
                          fontSize="small"
                          sx={{
                            color: (theme) =>
                              theme.palette.mode === 'light'
                                ? 'lightgray'
                                : 'gray',
                            ml: 1,
                          }}
                        />
                      </Tooltip>
                    </Box>
                    <Box
                      flexDirection={'row'}
                      display={'flex'}
                      alignItems={'center'}
                    >
                      <Typography
                        variant="h4"
                        color="textPrimary"
                        fontWeight="bold"
                      >
                        {data
                          ? formatNumber(
                              data?.EnergySummary?.TotalEnergyUsage.toFixed(0),
                            )
                          : '-'}{' '}
                        kWh
                      </Typography>
                      <Typography
                        variant="h4"
                        color="textPrimary"
                        fontWeight="bold"
                      ></Typography>
                    </Box>

                    <Typography
                      variant="body2"
                      sx={{ color: 'text.secondary' }}
                    >
                      {t('comparison_analysis.in_period')}{' '}
                      {selectedRange[0] && selectedRange[1]
                        ? `${formatDate(selectedRange[0])} - ${formatDate(selectedRange[1])}`
                        : t('comparison_analysis.select_period')}
                    </Typography>
                  </Box>

                  <Box
                    display="flex"
                    flexDirection="row"
                    justifyContent="center"
                    alignItems="center"
                  >
                    <Tooltip
                      title={
                        aggregate === 'hour'
                          ? t(
                              'comparison_analysis.aggregate_change_unavailable',
                            )
                          : t('comparison_analysis.change_aggregate')
                      }
                      placement="top"
                    >
                      <div>
                        <Button
                          ref={anchorRef}
                          onClick={handleToggleAggregationMenu}
                          startIcon={<FunctionsRoundedIcon />}
                          disabled={aggregate === 'hour'}
                          sx={{
                            p: 1,
                            px: 2,
                            borderRadius: 3,
                            color: 'text.primary',
                            width: 'auto',
                            border: '1px solid',
                            borderColor: 'divider',
                            mr: 1,
                            textTransform: 'none', // optional: to keep label casing
                          }}
                        >
                          {dataSeriesAggregate?.label ?? 'Vælg'}
                        </Button>
                      </div>
                    </Tooltip>

                    <Popper
                      open={openAggregationMenu}
                      anchorEl={anchorRef.current}
                      placement="bottom-end"
                      transition
                      disablePortal
                      sx={{ zIndex: 1301 }}
                    >
                      {({ TransitionProps }) => (
                        <Grow
                          {...TransitionProps}
                          style={{ transformOrigin: 'left top' }}
                        >
                          <Paper
                            elevation={0}
                            variant="outlined"
                            sx={{ mt: 0.5, borderRadius: 2 }}
                          >
                            <ClickAwayListener
                              onClickAway={handleCloseAggregationMenu}
                            >
                              <MenuList
                                autoFocusItem={open}
                                dense
                                sx={{ minWidth: 150, p: 1 }}
                              >
                                {options.map((option) => (
                                  <MenuItem
                                    key={option.value}
                                    onClick={() =>
                                      handleSelectAggregation(option)
                                    }
                                    sx={{
                                      py: '6px',
                                      px: '12px',
                                      borderRadius: '5px',
                                      '&:hover': {
                                        bgcolor: 'action.hover',
                                        transition: 'all 0.3s ease',
                                      },
                                    }}
                                  >
                                    <Box
                                      component="span"
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                      }}
                                    >
                                      <Typography
                                        variant="body2"
                                        fontWeight="medium"
                                      >
                                        {option.label}
                                      </Typography>
                                    </Box>
                                  </MenuItem>
                                ))}
                              </MenuList>
                            </ClickAwayListener>
                          </Paper>
                        </Grow>
                      )}
                    </Popper>
                    <ToggleButtonGroup
                      className="non-printable"
                      value={viewMode}
                      exclusive
                      onChange={(event, newView) => {
                        if (
                          newView !== null &&
                          validChartOptions.includes(newView)
                        ) {
                          setViewMode(newView);
                        }
                      }}
                    >
                      <Tooltip
                        title={
                          !validChartOptions.includes('line')
                            ? t(
                                'comparison_analysis.not_available_for_selected_period',
                              )
                            : t(
                                'comparison_analysis.display_sum_of_consumption_over_time',
                              )
                        }
                        placement="top"
                      >
                        <span>
                          <ToggleButton
                            value="line"
                            size="small"
                            disabled={!validChartOptions.includes('line')}
                            sx={{ borderRadius: 3, p: 1.1 }}
                          >
                            <ShowChartRoundedIcon />
                          </ToggleButton>
                        </span>
                      </Tooltip>

                      <Tooltip
                        title={t(
                          'comparison_analysis.display_sum_of_consumption_over_time',
                        )}
                        placement="top"
                      >
                        <span>
                          <ToggleButton
                            value="column"
                            size="small"
                            disabled={!validChartOptions.includes('column')}
                            sx={{ borderRadius: 3, p: 1.1 }}
                          >
                            <BarChartRoundedIcon />
                          </ToggleButton>
                        </span>
                      </Tooltip>
                    </ToggleButtonGroup>

                    <ToggleButtonGroup
                      value={aggregate}
                      exclusive
                      onChange={(event, newValue) => {
                        if (newValue !== null) setAggregate(newValue);
                      }}
                      fullWidth
                      sx={{ ml: 1 }}
                    >
                      {['hour', 'day', 'week', 'month', 'year'].map((item) => {
                        const isDisabled = !validXAxisOptions.includes(item);
                        return (
                          <Tooltip
                            key={item}
                            title={getTooltipTitle(item)}
                            placement="top"
                          >
                            {/* Wrap in span to make Tooltip work on disabled buttons */}
                            <span>
                              <ToggleButton
                                value={item}
                                size="medium"
                                disabled={isDisabled}
                                sx={{
                                  textTransform: 'none',
                                  borderRadius: 3,
                                  p: 1.1,
                                  pointerEvents: isDisabled ? 'none' : 'auto', // important for Tooltip to show
                                }}
                              >
                                {item === 'hour' && 'Time'}
                                {item === 'day' && 'Dag'}
                                {item === 'week' && 'Uge'}
                                {item === 'month' && 'Måned'}
                                {item === 'year' && 'År'}
                              </ToggleButton>
                            </span>
                          </Tooltip>
                        );
                      })}
                    </ToggleButtonGroup>

                    <Tooltip
                      className="non-printable"
                      title={`${filtersOpen ? t('common.hide') : t('common.show')}`}
                      placement="top"
                    >
                      <IconButton
                        onClick={() => setFiltersOpen(!filtersOpen)}
                        sx={{
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 3,
                          ml: 4,
                        }}
                      >
                        {filtersOpen ? (
                          <KeyboardArrowRightRoundedIcon />
                        ) : (
                          <KeyboardArrowLeftRoundedIcon />
                        )}
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </Stack>
              <Suspense fallback={<div>Loading...</div>}>
                <EnergyAnalysisComparisonSeriesChart // Comparison Series Chart to compare data accross multiple nodes
                  energyConsumptionData={data ?? []}
                  dataSeriesAggregate={dataSeriesAggregate}
                  comparisonEnergyConsumptionData={
                    comparisonEnergyConsumptionData
                  }
                  node={nodeToAnalyse}
                  isLoading={
                    isLoadingEnergyConsumptionData ||
                    isFetchingEnergyConsumptionData
                  }
                  viewMode={viewMode}
                  aggregate={aggregate}
                  selectedEnergyGroup={selectedEnergyGroup}
                  comparisonItems={comparisonItems}
                  comparisonFilters={comparisonFilters}
                />
              </Suspense>
            </CardContent>
          </Card>
        </Grid>

        {filtersOpen && (
          <Grid
            item
            className="non-printable"
            md={12}
            lg={3}
            sx={{
              transition: 'flex-basis 0.3s ease', // smooth collapse/expand
            }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Card variant="outlined" sx={{ borderRadius: 2 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography
                        variant="body1"
                        color="textSecondary"
                        fontWeight="bold"
                      >
                        {t('comparison_analysis.filtering')}
                      </Typography>
                      <Tooltip
                        title={t('comparison_analysis.use_filters')}
                        placement="right"
                      >
                        <InfoRoundedIcon
                          fontSize="small"
                          sx={{
                            color: (theme) =>
                              theme.palette.mode === 'light'
                                ? 'lightgray'
                                : 'gray',
                            ml: 1,
                          }}
                        />
                      </Tooltip>
                    </Box>
                    <Suspense fallback={<div>Loading...</div>}>
                      <EnergyAnalyzerFilterInput
                        handleApply={handleApplyComparisonFilter}
                        setComparisonFilters={setComparisonFilters}
                        selectedEnergyGroup={selectedEnergyGroup}
                        startHour={
                          selectedEnergyGroup?.options?.workingDayStart
                        }
                        endHour={selectedEnergyGroup?.options?.workingDayEnd}
                      />
                    </Suspense>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Card variant="outlined" sx={{ borderRadius: 2 }}>
                  <CardContent>
                    <EnergyTreeNodeAutocomplete
                      nodeOptions={selectedEnergyGroup?.energyTreeNodes}
                      selectedNode={nodeToAnalyse}
                      onChange={(newValue) => {
                        if (newValue) setNodeToAnalyse(newValue);
                      }}
                    />
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{ mt: 1, mb: 1 }}
                    >
                      {`${t('comparison_analysis.compare_consumption_with')} ${nodeToAnalyse?.name} ${t('comparison_analysis.and_other_measurement_points')}`}
                    </Typography>
                    <Grid
                      container
                      alignItems="center"
                      spacing={2}
                      sx={{ mb: 2 }}
                    >
                      <Grid item xs={12}>
                        <Suspense fallback={<div>Loading...</div>}>
                          <SingleDateSelector
                            setComparisonRange={setComparisonTimeRange}
                            globalDuration={duration}
                            timeRange={timeRange}
                            isComparingSameNode={isComparingSameNode}
                            comparisonItems={comparisonItems}
                            aggregate={aggregate}
                          />
                        </Suspense>
                      </Grid>

                      <Grid item xs>
                        <Button
                          onClick={addComparisonItem}
                          variant="outlined"
                          fullWidth
                          sx={{
                            color: 'text.primary',
                            textTransform: 'none',
                            p: 2,
                            borderRadius: 3,
                            borderColor: 'divider',
                            ':hover': {
                              color: 'success.light',
                              borderColor: 'divider',
                            },
                          }}
                          startIcon={<AddRoundedIcon />}
                          // InputAdornmentProps={{ position: 'start' }}
                          disabled={comparisonItems
                            .slice(0)
                            .some((item) => item.node === null)}
                        >
                          {' '}
                          {t('comparison_analysis.add_measurement_point')}
                        </Button>
                      </Grid>
                    </Grid>

                    {comparisonItems.map((item, index) => (
                      <React.Fragment key={index}>
                        <Grid
                          container
                          alignItems="center"
                          spacing={2}
                          sx={{ mb: 1 }}
                        >
                          <Grid item xs>
                            <EnergyTreeNodeAutocomplete
                              nodeOptions={selectedEnergyGroup?.energyTreeNodes}
                              selectedNode={item.node}
                              onChange={(newValue) =>
                                updateComparisonItem(index, 'node', newValue)
                              }
                            />
                          </Grid>

                          <Grid item>
                            <IconButton
                              onClick={() => removeComparisonItem(index)}
                              sx={{
                                p: 2,
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 3,
                                ':hover': { color: 'error.light' },
                              }}
                            >
                              <DeleteRoundedIcon />
                            </IconButton>
                          </Grid>
                        </Grid>
                      </React.Fragment>
                    ))}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default ComparisonAnalysisTab;
